import { useState } from 'react';
import { Download, X } from 'lucide-react';
import PDFPreview from '@/components/common/PDFPreview';
import { saveRemoteFile } from '@/utils/saveFile';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';

interface Certificate {
  id: string;
  title: string;
  image_url: string;
  date: string;
}

interface CertificateModalProps {
  certificate: Certificate | null;
  onClose: () => void;
  /** Surfaces a failed save; the page owns the toast. */
  onError?: (message: string) => void;
}

export function CertificateModal({ certificate, onClose, onError }: CertificateModalProps) {
  const [saving, setSaving] = useState(false);

  const handleDownload = async () => {
    if (!certificate || saving) return;
    setSaving(true);
    try {
      await saveRemoteFile(certificate.image_url, { title: certificate.title });
    } catch {
      onError?.('Failed to download certificate');
    } finally {
      setSaving(false);
    }
  };

  if (!certificate) return null;

  return (
    <Flex
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.75)"
      justify="center"
      align="center"
      p="20px"
      zIndex="1000"
      onClick={onClose}
      role="presentation"
    >
      <Flex
        bg="white"
        borderRadius="16px"
        w="full"
        maxW="800px"
        maxH="90%"
        overflow="hidden"
        flexDir="column"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="certificate-modal-title"
      >
        <Flex justify="space-between" align="center" p="20px" borderBottom="1px solid" borderBottomColor="#e1e5eb">
          <Text id="certificate-modal-title" as="h2" fontSize="20px" fontWeight="semibold" color="volyx.text" m="0">
            {certificate.title}
          </Text>
          <Flex align="center" gap="2" flexShrink={0}>
            <chakra.button
              type="button"
              aria-label={`Download ${certificate.title}`}
              disabled={saving}
              p="5px"
              bg="transparent"
              border="none"
              cursor={saving ? 'default' : 'pointer'}
              opacity={saving ? 0.5 : 1}
              color="volyx.action"
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={() => { void handleDownload(); }}
            >
              <Download size={22} />
            </chakra.button>
            <chakra.button
              type="button"
              aria-label="Close certificate"
              p="5px"
              bg="transparent"
              border="none"
              cursor="pointer"
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={onClose}
            >
              <X size={24} />
            </chakra.button>
          </Flex>
        </Flex>
        <Flex p="20px" flexDir="column" align="center" overflowY="auto" flex="1" minH="0">
          <Box w="full" flex="1" minH="320px">
            <PDFPreview uri={certificate.image_url} variant="full" />
          </Box>
          <Text mt="16px" fontSize="16px" fontWeight="medium" color="#2c3a4a" mb="0">{certificate.date}</Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
